import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { CompletionStatus } from '@eika-felles/kyc-declaration-lib/dist/generated-types';

import {
    fetchApplication,
    FormTypeCompletionDetails,
    getOrCreateSblConsent,
    insuranceClickInc,
    kycError,
    KycErrorAction,
    loanCompletionDetailsNextEvent,
    openStep,
    sblFailed,
    setKycAnswered,
    updateApplicationKycNextEvent,
    UpdateConsent,
    updateConsent,
    updateStepsCompletionFetch,
} from '../../actions';
import { fieldUpdateInsurance } from '../../actions/completion';
import { DispatchWithState, StringKeyValue } from '../../actions/types';
import Loader from '../../components/Loader/Loader';
import ExpandableTableItems from '../../components/LoanApplication/ExpandableTableItems';
import ProductImage from '../../components/ProductImage/ProductImage';
import Step from '../../components/Steps/Step';
import StepCounter from '../../components/Steps/StepCounter';
import Steps from '../../components/Steps/Steps';
import { AppState } from '../../reducers';
import { ApplicationReducer } from '../../reducers/application';
import { CalculatorReducer } from '../../reducers/calculator';
import { CompletionReducer } from '../../reducers/completion';
import { ConsentReducer } from '../../reducers/consent';
import { ContextReducer } from '../../reducers/context';
import { FeatureToggleReducer } from '../../reducers/feature';
import { LoaderReducer } from '../../reducers/loader';
import { SblReducer } from '../../reducers/sbl';
import { StepsReducer } from '../../reducers/steps';
import { UserReducer } from '../../reducers/user';
import { VehicleReducer } from '../../reducers/vehicle';
import { getProductCompletionTitle, getProductIcon } from '../../util/mappings';
import { trackEvent, TrackingStep } from '../../util/piwik-utils';
import LoanCompletionDetails from './LoanCompletionDetails/LoanCompletionDetails';
import LoanCompletionInsurance from './LoanCompletionInsurance/LoanCompletionInsurance';
import LoanCompletionKyc from './LoanCompletionKyc/LoanCompletionKyc';
import LoanCompletionReceipt from './LoanCompletionReceipt/LoanCompletionReceipt';
import LoanCompletionSbl from './LoanCompletionSbl/LoanCompletionSbl';
import LoanCompletionSigning from './LoanCompletionSigning/LoanCompletionSigning';
import LoanCompletionStart from './LoanCompletionStart/LoanCompletionStart';

interface Props {
    action: {
        fetchApplication: () => DispatchWithState;
        loanCompletionDetailsNextEvent: (form: FormTypeCompletionDetails) => DispatchWithState;
        updateConsent: (name: UpdateConsent['name'], value: UpdateConsent['value']) => void;
        updateApplicationKycNextEvent: () => DispatchWithState;
        openStep: (step: number) => void;
        setKycAnswered: () => void;
        kycError: () => KycErrorAction;
        insuranceClickInc: () => void;
        getOrCreateSblConsent: () => DispatchWithState;
        sblFailed: () => void;
        fieldUpdateInsurance: (name: string, value: string) => void;
        updateStepsCompletionFetch: () => DispatchWithState;
    };
    application: ApplicationReducer;
    completion: CompletionReducer;
    calculator: CalculatorReducer;
    consent: ConsentReducer;
    context: ContextReducer;
    vehicle: VehicleReducer;
    loader: LoaderReducer;
    text: StringKeyValue;
    steps: StepsReducer;
    user: UserReducer;
    sbl: SblReducer;
    feature: FeatureToggleReducer;
}

class LoanCompletion extends Component<Props> {
    componentDidMount() {
        const { loader, action } = this.props;
        if (!loader.hasFetchedApplicationStatus) action.fetchApplication();
        action.updateStepsCompletionFetch();
    }

    render() {
        const {
            loader,
            application,
            completion,
            calculator,
            consent,
            context,
            vehicle,
            action,
            user,
            steps,
            text,
            sbl,
            feature,
        } = this.props;

        const { openStep, updateApplicationKycNextEvent, setKycAnswered, kycError } = action;
        const { isFetchingApplication, hasFetchedApplication, hasNotYetUpdatedCompletionSteps } = loader;
        const { applicationErrorNotFound } = application;
        const { loanInfo } = calculator;
        const { loanType, loanProduct } = loanInfo;
        const step = steps?.steps;
        const loanProductTitle = getProductCompletionTitle(loanType, loanProduct, text);
        const kycBackButtonStep = consent.data.length === 0 && completion.ongoing.customerIsMainApplicant ? 1 : 4;

        if (isFetchingApplication || hasNotYetUpdatedCompletionSteps)
            return <Loader title={text.loadingApplicationStatus} />;

        return (
            <div className="loan-completion">
                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                    <>
                        {step[0].open ? (
                            <ProductImage
                                className="loan-completion__intro"
                                image={getProductIcon(loanType, loanProduct)}
                                title={loanProductTitle && loanProductTitle}
                            />
                        ) : (
                            <>{loanProductTitle && <h1 className="text-center">{loanProductTitle}</h1>}</>
                        )}

                        {hasFetchedApplication ? (
                            <>
                                {step && (
                                    <Steps>
                                        <Step open={step[0].open}>
                                            <StepCounter currentStep="1" totalSteps="7" />
                                            <LoanCompletionStart
                                                action={action}
                                                completion={completion}
                                                calculator={calculator}
                                                vehicle={vehicle}
                                                feature={feature}
                                                text={text}
                                                sbl={sbl}
                                            />
                                        </Step>
                                        <Step open={step[1].open}>
                                            <StepCounter currentStep="2" totalSteps="7" />
                                            <LoanCompletionSbl
                                                action={action}
                                                loader={loader}
                                                text={text}
                                                sbl={sbl}
                                                completion={completion}
                                            />
                                        </Step>

                                        <Step open={step[2].open}>
                                            <StepCounter currentStep="3" totalSteps="7" />
                                            <LoanCompletionInsurance
                                                action={action}
                                                completion={completion}
                                                calculator={calculator}
                                                feature={feature}
                                                text={text}
                                            />
                                        </Step>
                                        <Step open={step[3].open}>
                                            <StepCounter currentStep="4" totalSteps="7" />
                                            <LoanCompletionDetails
                                                action={action}
                                                completion={completion}
                                                calculator={calculator}
                                                vehicle={vehicle}
                                                text={text}
                                                consent={consent}
                                            />
                                        </Step>
                                        <Step open={step[4].open}>
                                            <StepCounter currentStep="5" totalSteps="7" />
                                            <LoanCompletionKyc
                                                ssn={user.context.ssn}
                                                previousEvent={() => openStep(kycBackButtonStep)}
                                                completeEvent={(status: string) => {
                                                    trackEvent(TrackingStep.CompletionKyc);
                                                    if (status === CompletionStatus.FAILED) kycError();
                                                    else if (
                                                        status === CompletionStatus.OK ||
                                                        status === CompletionStatus.REQUIRES_MANUAL_CONTROL_BY_ADVISOR
                                                    ) {
                                                        setKycAnswered();
                                                        updateApplicationKycNextEvent();
                                                    }
                                                }}
                                                text={text}
                                                loader={loader}
                                            />
                                        </Step>

                                        <Step open={step[5].open}>
                                            <StepCounter currentStep="6" totalSteps="7" />
                                            <LoanCompletionSigning context={context} completion={completion} />
                                        </Step>

                                        <Step open={step[6].open}>
                                            <StepCounter currentStep="7" totalSteps="7" />
                                            <LoanCompletionReceipt
                                                text={text}
                                                completion={completion}
                                                calculator={calculator}
                                                isEika={context.config.bankregnr === '0770'}
                                                insuranceClick={this.props.action.insuranceClickInc}
                                            />
                                        </Step>
                                    </Steps>
                                )}
                            </>
                        ) : (
                            <>
                                {applicationErrorNotFound ? (
                                    <p>{text.completionApplicationNotFound}</p>
                                ) : (
                                    <p>{text.completionApplicationMissingCaseId}</p>
                                )}
                            </>
                        )}

                        <ExpandableTableItems text={text} />
                    </>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    text: state.context.config.data.text,
    application: state.application,
    completion: state.completion,
    calculator: state.calculator,
    consent: state.consent,
    context: state.context,
    vehicle: state.vehicle,
    loader: state.loader,
    steps: state.steps,
    user: state.user,
    kyc: state.kyc,
    sbl: state.sbl,
    feature: state.feature,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    action: bindActionCreators(
        {
            loanCompletionDetailsNextEvent,
            updateApplicationKycNextEvent,
            setKycAnswered,
            fetchApplication,
            updateConsent,
            openStep,
            kycError,
            insuranceClickInc,
            getOrCreateSblConsent,
            sblFailed,
            fieldUpdateInsurance,
            updateStepsCompletionFetch,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(LoanCompletion);
